import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsApi } from '../services/cloudApi';

/**
 * Plus durumunu tek merkezden sağlayan hook.
 * Tüm bileşenler isPlusUser yerine bu hook'u kullanmalı.
 *
 * Döndürdükleri:
 *   isPlus        — kullanıcı şu an aktif Plus mı
 *   plan          — 'free' | 'plus' | 'admin'
 *   source        — 'trial' | 'license' | 'admin' | null
 *   expiresAt     — Date | null
 *   remainingDays — kalan gün sayısı (null = ömürlük veya free)
 *   trialUsed     — deneme hakkı daha önce kullanıldı mı
 *   isTrial       — aktif üyelik deneme mi
 *   isLifetime    — ömürlük lisans mı
 *   loading       — ilk yükleme devam ediyor mu
 *   refresh()     — durumu yenile
 */
const usePlusStatus = () => {
  const { user, authToken } = useAuth();
  const [status, setStatus] = useState({
    isPlus: false,
    plan: 'free',
    source: null,
    expiresAt: null,
    remainingDays: null,
    trialUsed: false,
    isTrial: false,
    isLifetime: false,
    loading: true,
  });

  const fetchRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!authToken || !user) {
      setStatus((s) => ({
        ...s,
        isPlus: false,
        plan: 'free',
        source: null,
        expiresAt: null,
        remainingDays: null,
        trialUsed: false,
        isTrial: false,
        isLifetime: false,
        loading: false,
      }));
      return;
    }

    try {
      const data = await subscriptionsApi.getMe({ token: authToken });
      if (data?.success) {
        setStatus({
          isPlus: data.isPlus === true,
          plan: data.plan || 'free',
          source: data.source || null,
          expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
          remainingDays: data.remainingDays ?? null,
          trialUsed: data.trialUsed === true,
          isTrial: data.source === 'trial',
          isLifetime: data.isPlus === true && data.plan === 'plus' && !data.expiresAt,
          loading: false,
        });
      } else {
        setStatus((s) => ({ ...s, loading: false }));
      }
    } catch {
      setStatus((s) => ({ ...s, loading: false }));
    }
  }, [authToken, user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Rol güncellenince yenile
  useEffect(() => {
    const onRoleUpdated = () => fetchStatus();
    window.addEventListener('notesplus-role-updated', onRoleUpdated);
    window.addEventListener('notesplus-refresh-user', onRoleUpdated);
    return () => {
      window.removeEventListener('notesplus-role-updated', onRoleUpdated);
      window.removeEventListener('notesplus-refresh-user', onRoleUpdated);
    };
  }, [fetchStatus]);

  return { ...status, refresh: fetchStatus };
};

export default usePlusStatus;
